import { serviceApi, withServiceRequestDefaults } from 'Shared/network';
import {
  FacebookBehavior,
  InstagramBehavior,
  NblogBehavior,
  NkinBehavior,
  NplaceBehavior,
  ThreadsBehavior,
  YoutubeBehavior,
} from 'viewup-api/v2';

/**
 * 백엔드 계약을 **SDK 타입 그대로** 받는다.
 *
 * 이전에는 플랫폼 무관 통합 enum(`ClickViewingCampaignType`)을 그대로 썼고 `nplace` / `nkin`
 * 키는 아예 없어서, 호출부가 `as GetClickCampaignsReq` 캐스팅으로 통과시켰다. 그래서
 * `nplace=like` 같은 플랫폼 위반 값이 타입으로 걸리지 않고 400
 * (`each value in nplaceBehaviors must be one of ...`)으로만 드러났다.
 */
export type GetClickCampaignsReq = {
  facebook?: FacebookBehavior[];
  instagram?: InstagramBehavior[];
  nblog?: NblogBehavior[];
  nkin?: NkinBehavior[];
  nplace?: NplaceBehavior[];
  threads?: ThreadsBehavior[];
  youtube?: YoutubeBehavior[];
};

export type GetClickCampaignsRes = {
  imageUrl: string;
  openType: string;
  rewardPoint: number;
  type: string;
  uuid: string;
}[];

/**
 * 선택 목록에서 해당 플랫폼이 허용하는 behavior 만 남긴다.
 *
 * 허용 집합을 손으로 적은 리터럴로 두면 SDK 가 바뀌어도 조용히 어긋난다 —
 * 반드시 SDK enum 을 펼쳐서 쓴다.
 */
const pickAllowed = <T extends string>(
  allowed: readonly T[],
  selected: readonly string[] | undefined,
): T[] | undefined => {
  const picked = (selected ?? []).filter((behavior): behavior is T =>
    (allowed as readonly string[]).includes(behavior),
  );

  return picked.length > 0 ? picked : undefined;
};

/**
 * 스토어의 플랫폼 무관 선택값을 백엔드 계약에 맞는 요청 객체로 좁힌다.
 *
 * `ClickViewingCampaignType` 은 플랫폼 제약을 표현하지 못하는 통합 enum이라
 * 타입만으로는 `nplace=like` 같은 조합을 막을 수 없다. 이 함수가 마지막 방어선이다.
 */
export const toGetClickCampaignsReq = (
  selected: Partial<Record<string, readonly string[]>>,
): GetClickCampaignsReq => ({
  facebook: pickAllowed(Object.values(FacebookBehavior), selected.facebook),
  instagram: pickAllowed(Object.values(InstagramBehavior), selected.instagram),
  nblog: pickAllowed(Object.values(NblogBehavior), selected.nblog),
  nkin: pickAllowed(Object.values(NkinBehavior), selected.nkin),
  nplace: pickAllowed(Object.values(NplaceBehavior), selected.nplace),
  threads: pickAllowed(Object.values(ThreadsBehavior), selected.threads),
  youtube: pickAllowed(Object.values(YoutubeBehavior), selected.youtube),
});

/** 아무것도 선택되지 않았을 때의 전체 조회 파라미터. */
const allBehaviors = (): GetClickCampaignsReq => ({
  facebook: Object.values(FacebookBehavior),
  instagram: Object.values(InstagramBehavior),
  nblog: Object.values(NblogBehavior),
  nkin: Object.values(NkinBehavior),
  nplace: Object.values(NplaceBehavior),
  threads: Object.values(ThreadsBehavior),
  youtube: Object.values(YoutubeBehavior),
});

export const getClickCampaigns = async (
  params: GetClickCampaignsReq,
): Promise<GetClickCampaignsRes> => {
  const hasValidParams = Object.values(params).some((value) => value && value.length > 0);

  const req = hasValidParams
    ? Object.entries(params)
        .filter(([_, value]) => value && value.length > 0)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    : allBehaviors();

  const response = await serviceApi.campaign.getAvailableClickCampaign(
    withServiceRequestDefaults({
      ...req,
    }),
  );

  return response.data.data;
};
